import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';
import LearnTrapBuyExampleCard from '@/components/LearnTrapBuyExampleCard';

const FAQS = [
  {
    q: 'What is the Dividend Triangle?',
    a: 'DiviDrip\'s proprietary dividend-quality score that combines three growth legs — Revenue, EPS, and Dividend — weighted by payout-ratio stability. Each leg is normalised to a 0-10 scale; the geometric mean gives an overall score. Stocks where all three legs are healthy get a high score; those where the legs disagree (e.g. dividend rising while EPS falls) get a TRAP / WEAK / RISK flag.',
  },
  {
    q: 'What do TRAP, WEAK, and RISK flags mean?',
    a: 'TRAP — dividend is rising rapidly while underlying earnings are flat/falling. The dividend is being funded by debt or accounting tricks; cut likely. WEAK — all three growth legs are positive but very slow (under 3% each); income is real but compounding will be sluggish. RISK — payout ratio is dangerously high (>90% non-REIT, >100% REIT); one bad quarter could force a cut.',
  },
  {
    q: 'Should I avoid any stock with a flag?',
    a: 'Not automatically. Flags are warnings, not absolutes. A TRAP flag on a quality REIT during a temporary FFO dip is different from a TRAP on a struggling legacy telecom. Use the flag as a trigger to read the underlying business — if you can explain why the flag is temporary, you can hold. If you can\'t, you probably shouldn\'t.',
  },
  {
    q: 'Why use a geometric mean instead of an average?',
    a: 'The geometric mean punishes weak legs more heavily than an arithmetic average. A stock scoring 10/10/0 on Revenue/EPS/Dividend gets an arithmetic average of 6.7 (looks great) but a geometric mean of 0 (correctly: no dividend growth = no Dividend Triangle thesis). Geometric mean prevents one strong leg from masking a fatal weakness in another.',
  },
  {
    q: 'Where do I see the Triangle score in DiviDrip?',
    a: 'Open any stock\'s Stock Modal and click the dedicated Triangle tab. That tab shows the three individual growth legs broken out (Revenue, EPS, Dividend) plus the payout-ratio modifier, so you can see exactly which dimension is driving the score and any flags. The Triangle lives on its own tab — it isn\'t in the main stock table or the Screener.',
  },
  {
    q: 'Why does the Triangle behave differently for REITs, MLPs, and ETFs?',
    a: 'The three-leg framework (Revenue / EPS / Dividend) was designed for regular C-corps. ETFs don\'t have any of those at the fund level, so they get a single-leg distribution-only score with a sky-blue "use Div Growth + Fund Profile" label. REITs and MLPs do report GAAP revenue and EPS, but their EPS is depressed by heavy non-cash depreciation (on buildings or pipelines) — so the Triangle suppresses the dividend-trap warning for both, redirects the payout-ratio bullet to FFO (REITs) or DCF (MLPs), and surfaces a colored ribbon at the top of the Triangle tab pointing at the Cash Lens panel on the Stock Metrics tab. For C-corps, no special handling — the Triangle reads as designed.',
  },
];

export default function LearnDividendTriangle() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="Reading the Dividend Triangle Score — What the Flags Really Mean | DiviDrip"
      description="Deep dive on DiviDrip's proprietary Dividend Triangle scoring system. How the three growth legs are weighted, what TRAP/WEAK/RISK flags signal, and how to read the score in 10 seconds."
      canonical="https://twylightcrow.com/learn/dividend-triangle"
      guidePath="/learn/dividend-triangle"
      sectionLabel="Metrics & Evaluation"
      sectionAnchor="metrics"
      kicker="Learn · DiviDrip tools"
      h1="The Dividend Triangle — Score, Flags, and Quick Reading"
      datePublished="2026-05-23"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/five-numbers-to-check', label: 'The 5 numbers I check before any dividend buy' },
        { to: '/learn/dividend-traps', label: 'Dividend trap stocks — spotting yields that lie' },
        { to: '/learn/evaluating-dividend-etfs', label: 'How to evaluate dividend ETFs' },
        { to: '/learn/reits-vs-bdcs-vs-cefs', label: 'REITs vs BDCs vs CEFs — high-yield vehicles' },
        { to: '/learn/mlps-explained', label: 'MLPs explained — high yield, K-1 forms' },
        { to: '/learn/dividend-kings-vs-aristocrats-vs-achievers', label: 'Dividend Kings vs Aristocrats vs Achievers' },
        { to: '/learn/yield-on-cost', label: 'Yield on cost — your real long-term yield' },
        { to: '/glossary', label: 'Glossary — every dividend term defined' },
      ]}
    >
      <p>
        The Dividend Triangle is the metric I built for myself, then realised every
        dividend investor should have. It answers the single most important question:
        is this dividend SAFE? — in a single number from 0 to 10.
      </p>
      <p>
        Here&apos;s how it works, what the flags mean, and how to read it without
        thinking.
      </p>

      <aside className="not-prose my-6 rounded-lg border border-sky-300/40 dark:border-sky-700/40 bg-sky-50/60 dark:bg-sky-950/30 p-4">
        <p className="text-sm font-semibold text-sky-900 dark:text-sky-200 mb-1">
          Triangle behaviour differs by vehicle type
        </p>
        <p className="text-sm text-sky-900/85 dark:text-sky-200/85">
          The Triangle is built for single-name C-corps. ETFs, REITs, and MLPs each
          get a slightly different scoring treatment because their underlying
          fundamentals don&apos;t map cleanly to revenue + EPS + dividend. Skip
          ahead to{' '}
          <a href="#vehicle-aware-scoring" className="underline font-semibold">
            Vehicle-aware scoring
          </a>{' '}
          for the side-by-side table, or read the
          {' '}<Link to="/learn/evaluating-dividend-etfs" className="underline">ETF</Link>{' '},
          {' '}<Link to="/learn/reits-vs-bdcs-vs-cefs" className="underline">REIT</Link>, and
          {' '}<Link to="/learn/mlps-explained" className="underline">MLP</Link>{' '}
          deep-dives for the full story.
        </p>
      </aside>

      <h2>The three legs</h2>

      <h3>Leg 1 — Revenue Growth (3-year CAGR)</h3>
      <p>
        Top-line trajectory. Without revenue growth, EPS growth eventually stalls and
        dividend growth runs out of room. A company growing revenue 5% annually has
        permanent fuel for dividend hikes; one shrinking revenue does not.
      </p>

      <h3>Leg 2 — EPS Growth (3-year CAGR)</h3>
      <p>
        Bottom-line trajectory. EPS growth above revenue growth signals operating
        leverage or buybacks (good). EPS growth far above revenue growth via aggressive
        buybacks alone (when revenue is flat) is a yellow flag — you're running out
        of share-count reduction runway.
      </p>

      <h3>Leg 3 — Dividend Growth (5-year CAGR)</h3>
      <p>
        The whole point. The leg dividend investors care about most. But a strong
        Leg 3 with weak Legs 1 and 2 is the textbook dividend trap — the company is
        raising the payout from a shrinking pie. Eventually the pie runs out.
      </p>

      <h2>The payout-ratio modifier</h2>
      <p>
        Three healthy growth legs aren't enough on their own. A company with 8%
        revenue growth, 12% EPS growth, and 10% dividend growth that has a 95%
        payout ratio is one bad quarter from a cut. The Triangle's payout-ratio
        modifier discounts the score for stocks above industry-typical payout-ratio
        thresholds:
      </p>
      <ul>
        <li>REITs: &gt; 100% FFO payout ratio = penalty</li>
        <li>BDCs: &gt; 110% NII payout ratio = penalty</li>
        <li>Standard corporates: &gt; 80% earnings payout ratio = penalty</li>
        <li>Utilities: &gt; 80% earnings payout ratio = penalty</li>
      </ul>

      <h2 id="vehicle-aware-scoring">Vehicle-aware scoring — when the Triangle bends the rules</h2>
      <p>
        The three-leg framework was designed around regular C-corps that report
        clean GAAP revenue, EPS, and dividends. ETFs don&apos;t have any of those
        at the fund level. REITs and MLPs do, but their EPS is structurally
        depressed by non-cash depreciation, which produces scary-looking trap
        signals on names that are perfectly healthy. DiviDrip handles each case
        differently:
      </p>
      <div className="not-prose overflow-x-auto my-4">
        <table className="learn-table w-full text-sm">
          <thead>
            <tr>
              <th className="text-left">Vehicle</th>
              <th className="text-left">Triangle behaviour</th>
              <th className="text-left">What to anchor on instead</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Regular stock (C-corp)</strong></td>
              <td>Standard 3-leg score. TRAP / WEAK / RISK flags active. Payout
                  ratio &gt;100% surfaces as a red-flag bullet in the narrative.</td>
              <td>Triangle Score itself + payout ratio + FCF margin. The five
                  numbers in our{' '}
                  <Link to="/learn/five-numbers-to-check">pre-buy checklist</Link>{' '}
                  apply cleanly.</td>
            </tr>
            <tr>
              <td><strong>REIT</strong> (O, AMT, EQIX, STAG, PLD…)</td>
              <td>EPS leg unreliable — building depreciation drags GAAP earnings
                  to near-zero. Trap-flag detection <strong>suppressed</strong>.
                  Payout-ratio bullet redirects to FFO instead of EPS. A violet
                  &quot;REIT note&quot; ribbon appears at the top of the Triangle tab.</td>
              <td>The <strong>REIT Cash Lens</strong> panel on the Stock Metrics
                  tab. Uses FFO (Net Income + Depreciation/Amortization,
                  NAREIT-standard) divided by shares for the headline FFO/share
                  figure, then FFO payout ratio against the annual dividend.
                  Sweet Spot band is 70-85% FFO payout.</td>
            </tr>
            <tr>
              <td><strong>MLP</strong> (ET, EPD, MPLX, WES, BIP…)</td>
              <td>Same EPS depression as REITs but driven by pipeline depreciation.
                  Trap-flag detection <strong>suppressed</strong>. Payout-ratio
                  bullet cites DCF (Distributable Cash Flow), not EPS. An amber
                  &quot;MLP note&quot; ribbon appears at the top of the Triangle tab.</td>
              <td>The <strong>MLP Cash Lens</strong> panel on the Stock Metrics
                  tab. Same Net Income + D&amp;A construction, labelled as a DCF
                  proxy. Sweet Spot band is 70-85% DCF payout; healthy MLPs target
                  1.2&times; coverage (≤ ~83% payout) in their own supplementals.</td>
            </tr>
            <tr>
              <td><strong>ETF</strong> (SCHD, VYM, JEPI, DGRO…)</td>
              <td>Single-leg distribution-only score. Revenue + EPS legs are
                  structurally zero. Verdict label swaps to{' '}
                  <em>&quot;Distribution-only score · use Div Growth + Fund Profile&quot;</em>.
                  Radar fills sky-blue instead of red even for low scores. Trap
                  detection suppressed (depends on EPS coverage ETFs don&apos;t have).</td>
              <td>The Distribution Growth tab + Fund Profile (expense ratio, AUM,
                  fund family). See{' '}
                  <Link to="/learn/evaluating-dividend-etfs">How to Evaluate
                  Dividend ETFs</Link>{' '}for the five metrics that actually move
                  the needle on a fund.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-sm text-muted-foreground">
        <strong>Rule of thumb:</strong> if the ticker carries a colored note
        ribbon at the top of the Triangle tab, the EPS leg is the wrong yardstick
        and the Cash Lens panel (or Fund Profile, for ETFs) is what you should
        actually be reading. The Triangle Score remains useful as a coarse quality
        filter — just don&apos;t over-index on the EPS leg or any suppressed trap
        warning.
      </p>

      <h2>The flag taxonomy</h2>

      <h3>🟢 No flag (score 6+)</h3>
      <p>
        All three legs positive and growing, payout ratio reasonable. The Triangle
        is healthy. You don't need to investigate further; just decide if the yield
        + sector fit your portfolio needs.
      </p>

      <h3>🟡 WEAK (score 4-6)</h3>
      <p>
        All three legs positive but slow (under 3% each). The dividend is real and
        safe; the compounding will just be sluggish. Often signals a mature business
        in a slow-growth industry. Acceptable as a small portfolio position, not a
        core holding.
      </p>

      <h3>🟠 RISK (any score, high payout ratio)</h3>
      <p>
        Payout ratio is in the danger zone. The Triangle legs may all be growing, but
        the company has minimal room for error. One bad quarter, recession, or
        unexpected expense forces a cut. Common in over-leveraged REITs and BDCs.
      </p>

      <h3>🔴 TRAP (Leg 3 strongly positive, Legs 1+2 negative)</h3>
      <p>
        The classic dividend trap profile: dividend rising rapidly while underlying
        earnings are flat or shrinking. The company is funding the dividend with
        debt, asset sales, or accounting tricks. Cut is usually 12-36 months away.
        AT&T was the textbook TRAP signal for years before its 2022 cut.
      </p>

      <h2>How to read it in 10 seconds</h2>
      <ol>
        <li>Look at the overall score: 7+ = healthy, 4-6 = WEAK, below 4 = avoid unless you have a specific thesis.</li>
        <li>Check for a flag: any flag = pause and investigate before buying.</li>
        <li>Look at the three legs individually if anything looks off. If one leg is the entire problem (e.g. Revenue Growth -8% but EPS and Dividend strong), check whether it's a one-off (divestiture, COVID hit, FX) or structural.</li>
      </ol>

      <h2 id="buy-checklist">DiviDrip's Buy Checklist (the 5-point screen)</h2>
      <p>
        Below the Triangle radar chart you'll find <strong>DiviDrip's Buy Checklist</strong> — a
        five-point screen that translates the Triangle score and surrounding fundamentals into a
        simple <strong>5/5 · 4/5 · 3/5 · ≤2/5</strong> verdict. The checks adapt by asset class so the
        same scorecard reads sensibly for a C-corp, REIT, BDC, MLP, or income ETF.
      </p>
      <ul>
        <li><strong>Chowder Score</strong> at the top — current yield + 5-year dividend CAGR. Asset-class-aware thresholds: <strong>≥12</strong> for low-yield blue-chips, <strong>≥8</strong> for typical mid-yield names and REITs. Failing Chowder means the yield is being bought, not grown.</li>
        <li><strong>FCF coverage / FFO payout / NII coverage</strong> — proves the dividend is funded by actual cash, not debt or asset sales.</li>
        <li><strong>Payout ratio</strong> — earnings room for future hikes (target depends on sector and asset class).</li>
        <li><strong>Dividend streak / Distribution streak</strong> — track record through varied conditions.</li>
        <li><strong>Leverage (D/E)</strong> or <strong>AUM</strong> — debt load for C-corps, fund-closure risk for ETFs/CEFs.</li>
        <li><strong>Yield sanity</strong> or <strong>Yield in sector range</strong> — flags above- or below-band yields that usually signal distress or pricey growth.</li>
      </ul>
      <p>
        <strong>Reading the verdict:</strong> <span className="font-mono">5/5</span> = buy with conviction ·
        <span className="font-mono">4/5</span> = smaller starter position ·
        <span className="font-mono">3/5</span> = investigate and write your thesis ·
        <span className="font-mono">≤2/5</span> = walk away.
      </p>

      <h2 id="exec-summary">Executive Summary &amp; Compare Theses</h2>
      <p>
        The Buy Checklist verdict line carries two LLM-powered actions that turn the raw score into
        an investing brief you can actually act on:
      </p>
      <ul>
        <li>
          <strong>Write your thesis</strong> — opens DiviDrip's <em>Executive Summary</em>, a
          structured 5-section brief generated by <strong>Gemini 3 Flash</strong>:
          Executive Summary (2-3 sentence framing) · Buy Checklist Analysis · <strong>Bull Case</strong> ·
          <strong>Bear Case</strong> · <strong>What Changes Our Mind</strong> (the 2-3 milestones that
          would flip the verdict). Briefs are cached for 24 hours per ticker, so the first call is
          ~3-5 seconds and every subsequent open is instant.
        </li>
        <li>
          <strong>Compare Theses</strong> — opens a side-by-side modal with two columns of LLM
          briefs. Each column shows the same compact stat strip at the top (Score · Verdict · Yield
          · Chowder · Buy Checklist pass count) followed by the full brief. Use it to ask <em>"is
          KO or PEP a better entry today?"</em> or <em>"how does my Watchlist BDC stack up against
          the BDC I already own?"</em> The modal lives in <strong>4 places</strong>:
          <ul>
            <li>The Triangle tab inside any Stock Modal (Stock A is locked to that ticker).</li>
            <li>The standalone <code>/triangle/:ticker</code> page (also locked).</li>
            <li>The Watchlist Snapshot card on the dashboard (free-mode — pick any two tickers).</li>
            <li>The <strong>Tool Box</strong> on the Portfolio page (free-mode).</li>
          </ul>
        </li>
      </ul>
      <p>
        The Compare Theses modal remembers your <strong>last 5 ticker pairs</strong> as quick-reload
        chips. Pairs are stored locally — re-opens fall back to the 24-hour cache when fresh and
        re-run both briefs in parallel when stale (both columns show a spinning loader and
        <em>"Please wait, creating theses now…"</em> until each side resolves).
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>See it in DiviDrip</h2>
      <p>
        Open <Link to="/dashboard">DiviDrip</Link>, click any ticker to launch the
        Stock Modal, then open the dedicated <strong>Triangle</strong> tab. You&apos;ll
        see the three growth legs broken out, the payout-ratio modifier, the overall
        score, and any TRAP / WEAK / RISK flag — all on one screen. Treat it as the
        first thing you look at before a buy and the last thing you look at before
        a hold/sell decision.
      </p>
      <LearnTrapBuyExampleCard context="triangle" />
    </LearnPage>
  );
}
